import { type QueryObserverResult, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getRepositoriesMinimal } from './scm-client';
import type { Repository } from './scm-client.type';

/**
 * Hook to fetch minimal repositories (id and name) for a portfolio.
 *
 * @param {string | undefined} portfolioId - Portfolio ID to scope the repositories to
 * @param {Omit<UseQueryOptions<Repository[]>, 'queryKey' | 'queryFn'>} options - Optional React Query options
 * @returns Object containing the repositories array and the underlying query state
 */
function useRepositoriesMinimal(
  portfolioId: string | undefined,
  options?: Omit<UseQueryOptions<Repository[]>, 'queryKey' | 'queryFn'>,
): {
  repositories: Repository[];
  query: QueryObserverResult<Repository[]>;
} {
  const query = useQuery<Repository[]>({
    queryKey: ['scm', 'repositories', 'minimal', portfolioId],
    queryFn: () => getRepositoriesMinimal(portfolioId!),
    ...options,
    enabled: !!portfolioId && options?.enabled !== false,
  });

  return {
    repositories: query.data || [],
    query,
  };
}

export { useRepositoriesMinimal };
